<template>
  <div class="v2-events-catalog">
    <div class="v2-events-catalog__actions">
      <BaseButton
        class="v2-events-catalog__filters-trigger"
        variant="secondary"
        size="md"
        icon="mdi-filter-variant"
        @click="filtersOpen = true"
      >
        Filtros
        <span v-if="activeFiltersCount > 0" class="v2-events-catalog__filter-count">
          {{ activeFiltersCount }}
        </span>
      </BaseButton>
    </div>

    <div v-if="locked" class="v2-events-catalog__locked">
      <v-icon size="48" color="primary">mdi-lock-outline</v-icon>
      <h2 class="v2-headline v2-headline--display-md">Eventos bloqueados</h2>
      <p class="v2-body v2-body--md v2-body--muted">
        Los eventos están disponibles solo para miembros activos. Renueva tu membresía para continuar.
      </p>
      <BaseButton variant="amber" to="/member/renew" icon="mdi-refresh">
        Renovar membresía
      </BaseButton>
    </div>

    <div v-else class="v2-events-catalog__layout">
      <div class="v2-events-catalog__filters-col">
        <EventFilters
          v-model:search="filters.search"
          v-model:types="filters.types"
          v-model:categories="filters.categories"
          v-model:from="filters.from"
          v-model:to="filters.to"
          :category-options="filterOptions.categories"
          :mobile-open="filtersOpen"
          @clear="onClearFilters"
          @close="filtersOpen = false"
        />
      </div>

      <section class="v2-events-catalog__results">
        <div class="v2-events-catalog__results-head">
          <p class="v2-body v2-body--sm v2-body--muted">
            <template v-if="loadingEvents">Cargando eventos…</template>
            <template v-else-if="filteredEvents.length === 0">
              No hay eventos que coincidan con los filtros seleccionados
            </template>
            <template v-else>
              Mostrando {{ filteredEvents.length }} de {{ allEvents.length }} eventos
            </template>
          </p>
          <BaseButton
            v-if="hasActiveFilters"
            variant="ghost"
            size="sm"
            icon="mdi-close"
            @click="onClearFilters"
          >
            Limpiar filtros
          </BaseButton>
        </div>

        <div v-if="loadingEvents" class="v2-events-catalog__loading">
          <v-icon size="32" color="grey lighten-1">mdi-progress-clock</v-icon>
          <p class="v2-body v2-body--sm v2-body--muted">Buscando eventos disponibles…</p>
        </div>

        <div v-else-if="filteredEvents.length === 0" class="v2-events-catalog__empty">
          <EmptyState
            title="Sin eventos disponibles"
            description="No encontramos eventos con los filtros actuales. Ajusta los filtros o vuelve más tarde."
            icon="mdi-calendar-blank-outline"
          >
            <BaseButton v-if="hasActiveFilters" variant="primary" @click="onClearFilters">
              Limpiar filtros
            </BaseButton>
          </EmptyState>
        </div>

        <ul v-else class="v2-events-catalog__grid">
          <li v-for="ev in filteredEvents" :key="getEventId(ev)" class="v2-events-catalog__grid-item">
            <EventCard :event="ev" />
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { mapState, mapMutations } from "vuex";
import BaseButton from "@/components/memberDashboardV2/ui/BaseButton.vue";
import EmptyState from "@/components/memberDashboardV2/ui/EmptyState.vue";
import EventCard from "./EventCard.vue";
import EventFilters from "./EventFilters.vue";
import Endpoints from "@/share/Endpoints";
import {
  applyEventFilters,
  sortEventsByDate,
  extractEventOptions,
  getEventId,
  isEventLockedForUser,
} from "./eventsHelpers";

export default {
  name: "EventsCatalog",
  components: { BaseButton, EmptyState, EventCard, EventFilters },
  data() {
    return {
      loadingEvents: false,
      loadingFilters: false,
      allEvents: [],
      filterOptions: { categories: [], tags: [], types: [] },
      filters: {
        search: "",
        types: [],
        categories: [],
        from: "",
        to: "",
      },
      filtersOpen: false,
      organizationId: null,
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    ...mapState("membershipProfile", ["userMembership"]),
    locked() {
      return isEventLockedForUser(this.user);
    },
    filteredEvents() {
      return sortEventsByDate(
        applyEventFilters(this.allEvents, this.filters),
        "asc"
      );
    },
    hasActiveFilters() {
      return Boolean(this.filters.search)
        || this.filters.types.length > 0
        || this.filters.categories.length > 0
        || Boolean(this.filters.from)
        || Boolean(this.filters.to);
    },
    activeFiltersCount() {
      let count = 0;
      if (this.filters.search) count += 1;
      if (this.filters.types.length) count += this.filters.types.length;
      if (this.filters.categories.length) count += this.filters.categories.length;
      if (this.filters.from) count += 1;
      if (this.filters.to) count += 1;
      return count;
    },
  },
  methods: {
    ...mapMutations("notification", ["show"]),
    ...mapMutations("loader", ["loading", "loaded"]),
    getEventId,
    resolveOrganizationId() {
      if (!this.user) return null;
      if (this.userMembership && this.userMembership.organization) {
        return this.userMembership.organization._id || this.userMembership.organization.id;
      }
      if (this.user.headOffice) {
        if (typeof this.user.headOffice === "object") {
          return this.user.headOffice.id || this.user.headOffice._id;
        }
        return this.user.headOffice;
      }
      return null;
    },
    async loadEvents() {
      if (!this.organizationId) return;
      this.loadingEvents = true;
      try {
        const response = await Vue.axios({
          method: "GET",
          url: `${Endpoints.memberEvents}/${this.organizationId}`,
        });
        const events = response && response.data && response.data.events;
        this.allEvents = Array.isArray(events) ? events : [];
      } catch (e) {
        this.allEvents = [];
      } finally {
        this.loadingEvents = false;
      }
    },
    async loadFilterOptions() {
      if (!this.organizationId) return;
      this.loadingFilters = true;
      try {
        const response = await Vue.axios({
          method: "GET",
          url: `events/filters/${this.organizationId}`,
        });
        this.filterOptions = extractEventOptions(
          response && response.data && response.data.data
        );
      } catch (e) {
        this.filterOptions = { categories: [], tags: [], types: [] };
      } finally {
        this.loadingFilters = false;
      }
    },
    onClearFilters() {
      this.filters = {
        search: "",
        types: [],
        categories: [],
        from: "",
        to: "",
      };
    },
  },
  async mounted() {
    if (this.locked) return;
    this.organizationId = this.resolveOrganizationId();
    if (!this.organizationId) {
      this.show({
        text: "No se pudo identificar tu cámara. Intenta iniciar sesión nuevamente.",
        color: "error",
      });
      return;
    }
    this.loading();
    try {
      await Promise.all([this.loadEvents(), this.loadFilterOptions()]);
    } finally {
      this.loaded();
    }
  },
};
</script>

<style scoped>
.v2-events-catalog {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.v2-events-catalog__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
}

.v2-events-catalog__filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background-color: var(--color-amber);
  color: var(--color-amber-on);
  font-size: 11px;
  font-weight: var(--fw-bold);
  border-radius: var(--radius-full);
  margin-left: var(--space-1);
}

.v2-events-catalog__locked {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-12) var(--space-6);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  text-align: center;
}

.v2-events-catalog__layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-6);
}

@media (min-width: 1024px) {
  .v2-events-catalog__layout {
    grid-template-columns: 280px 1fr;
  }
}

.v2-events-catalog__filters-col {
  position: relative;
}

.v2-events-catalog__results {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
}

.v2-events-catalog__results-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  flex-wrap: wrap;
}

.v2-events-catalog__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-10);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
}

.v2-events-catalog__empty {
  padding: var(--space-4);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
}

.v2-events-catalog__grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
}

@media (min-width: 600px) {
  .v2-events-catalog__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1280px) {
  .v2-events-catalog__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.v2-events-catalog__grid-item {
  display: flex;
  min-width: 0;
}

.v2-events-catalog__grid-item > * {
  flex: 1 1 auto;
  width: 100%;
}

@media (max-width: 1023px) {
  .v2-events-catalog__filters-col {
    display: none;
  }

  .v2-events-catalog__filters-col:has(.v2-event-filters--open) {
    display: block;
  }

  .v2-events-catalog__filters-trigger {
    display: inline-flex;
  }
}

@media (min-width: 1024px) {
  .v2-events-catalog__filters-trigger {
    display: none;
  }
}
</style>
